/**
 * SurfaceWheel — the daily spin, as a prompt and a panel.
 *
 * Built to the design anchor's `.wfab` / `.whm` / `.wsp`. The prompt is the
 * whole notification: it appears when there is a spin to take and goes when it
 * has been used, so nothing else has to announce it.
 *
 * ## The server decides, the client animates
 *
 * The spin endpoint picks the outcome and names the winning SLICE; the client
 * turns the wheel to it (`useSpinSession` → `nextSpinRotation`). So what the
 * wheel stops on is what was actually awarded, and the client never receives
 * the odds. The result line waits for the animation rather than racing it — a
 * number that appears before the wheel stops tells the player the spin was
 * theatre.
 *
 * ## Three outcomes are not rewards
 *
 * A slice normally pays a RewardConfig. Three cannot be expressed that way —
 * one doubles what the player earns next, one hands the spin back, one takes
 * points away — so the response carries an `effect` and this reads it for the
 * result line. Without it, "2× next" would report as a win of nothing.
 */

import React from "react";
import {useSpinSession} from "@/hooks/games/useSpinSession";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {useSpinGamificationModel, SpinResult} from "@/hooks/gamification/useSpinGamificationModel";
import {useGetSpinWheelConfig} from "@/hooks/gamification/useGetSpinWheelConfig";
import {useGetMySpinWheelConfigId} from "@/hooks/gamification/useGetMySpinWheelConfigId";
import {SpinWheel} from "@/components/games/SpinWheel";
import "./SurfaceWheel.scss";

/** Matches the wheel's CSS transition, so the result lands with the animation. */
const SPIN_MS = 3800;

export interface SurfaceWheelProps {
    /** The WHEEL_SPIN model to spin. */
    modelId: string;
    /**
     * The model's PRIMARY wheel. Only a fallback for what to draw before the
     * per-member resolution arrives — see `useGetMySpinWheelConfigId` below.
     */
    spinWheelConfigId?: string;
    /** Spins the member has left. Zero renders nothing at all. */
    spinsRemaining: number;
    /** Above the title. "Daily spin" unless a surface has better words. */
    kicker?: string;
    /** The panel's headline. */
    title?: string;
    /** The prompt's label. */
    promptLabel?: string;
    /**
     * External open trigger — increment to open the modal (hero CTA, rail
     * entry). A counter rather than a boolean so repeated requests re-open
     * after the member closes it.
     */
    openRequest?: number;
}

/** The mini wheel on the prompt. Six wedges, turning. */
const PromptWheel: React.FC = () =>
    <svg className="sh-wfab__w" viewBox="0 0 100 100" aria-hidden="true">
        {/*
          * STRICT ALTERNATION, TWO FILLS. The wedges used to run
          * accent / p3 / o2 / p3 / accent / p1 — three different darks and two
          * different reds, so the soft-accent wedge read as a pale slice that
          * had been left unfinished rather than as part of a pattern. A wheel
          * is a pattern; the moment one wedge is a third colour the eye reads
          * it as meaning something.
          */}
        <path d="M50,50 L50,2 A48,48 0 0,1 91.57,26 Z" fill="var(--o)" />
        <path d="M50,50 L91.57,26 A48,48 0 0,1 91.57,74 Z" fill="var(--p1)" />
        <path d="M50,50 L91.57,74 A48,48 0 0,1 50,98 Z" fill="var(--o)" />
        <path d="M50,50 L50,98 A48,48 0 0,1 8.43,74 Z" fill="var(--p1)" />
        <path d="M50,50 L8.43,74 A48,48 0 0,1 8.43,26 Z" fill="var(--o)" />
        <path d="M50,50 L8.43,26 A48,48 0 0,1 50,2 Z" fill="var(--p1)" />
        <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="1.5" />
        <circle cx="50" cy="50" r="10" fill="var(--p1)" stroke="var(--o)" strokeWidth="1.5" />
    </svg>;

/**
 * What the result line says.
 *
 * The winning slice's own label is the headline — it is what the player just
 * watched the wheel stop on, and restating it in different words reads as a
 * different outcome. The sentence under it is what happened next, which is the
 * only part the effect changes.
 */
function resultDetail(result: SpinResult & { effect?: string; pointsRemoved?: number; multiplierExpiresAt?: number }): string {
    /* Deck-approved copy (RTTF_CONTENT_DECK.md §Spin outcome copy). */
    switch (result.effect) {
        case "POINT_MULTIPLIER": {
            /*
             * THE WINDOW IS CONFIG, SO THE SENTENCE READS IT.
             *
             * This said "the next hour" — the segment's `effectDurationMinutes`
             * written out as a word. Setting that field to 10 left the copy
             * promising an hour, which turned a config knob into a deploy. The
             * wording is ours; the number is the segment's.
             */
            const minutes = result.multiplierExpiresAt
                ? Math.max(1, Math.round((result.multiplierExpiresAt - Date.now()) / 60_000))
                : undefined;
            const window = minutes ? `${minutes} minutes` : "boost window";
            return `Every point you gain in the next ${window} will be doubled. Go get some Karma and rack up those points.`;
        }
        case "BONUS_SPIN":
            return "Give the wheel another spin for a chance at points.";
        case "POINT_PENALTY":
            return "Fortune wasn't in your favor today. Come back tomorrow for another chance at free points.";
        default:
            return result.rewardsGranted
                ? "Added to your total this week. See you tomorrow."
                : "Come back tomorrow for another chance at a point bump.";
    }
}

export const SurfaceWheel: React.FC<SurfaceWheelProps> = ({
    modelId, spinWheelConfigId, spinsRemaining,
    kicker = "Daily spin", title = "One spin a day", promptLabel = "Spin available",
    openRequest,
}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {mutateAsync: spin} = useSpinGamificationModel(modelId);
    /*
     * DRAW THE WHEEL THIS MEMBER IS ACTUALLY SPUN AGAINST.
     *
     * Prize-restricted members are routed server-side to the digital-only
     * global wheel (`resolveWheelConfigIdForUser`, shared with the spin
     * endpoint). Drawing the model's primary wheel showed them merch slices
     * they can never win — and, worse, a different slice count than the one the
     * outcome index refers to, so the wheel would stop on the wrong label. The
     * prop stays as the pre-resolution fallback so the panel is never empty.
     */
    const {data: resolvedWheel} = useGetMySpinWheelConfigId(modelId);
    const {data: wheelConfig, isLoading: wheelLoading, isError: wheelFailed} =
        useGetSpinWheelConfig(resolvedWheel?.spinWheelConfigId ?? spinWheelConfigId);

    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        if (openRequest) setOpen(true);
    }, [openRequest]);

    const segments = wheelConfig?.segments ?? [];
    const count = segments.length;

    /*
     * The spin sequence is shared with every other wheel — see `useSpinSession`.
     * This component owns the chrome and the copy; it does not own how a wheel
     * turns, which is what let this one drift from the working implementation.
     */
    const {rotation, spinning, result, error, onSpin: runSpin, reset} = useSpinSession({
        spin, segmentCount: count, animationMs: SPIN_MS,
    });

    const onSpin = React.useCallback(async () => {
        if (!fullToken?.token) return;
        await runSpin();
    }, [fullToken, runSpin]);

    /*
     * CLOSING CLEARS THE RESULT. A BONUS_SPIN outcome hands the spin back, so
     * `spinsRemaining` stays above zero and the prompt reappears — and without
     * this, reopening rendered the previous result panel with the Spin button
     * suppressed, leaving the member holding a spin they could not take.
     */
    const close = React.useCallback(() => {
        setOpen(false);
        reset();
    }, [reset]);

    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            /*
             * Escape closes even mid-spin. Blocking the exits while the wheel
             * turns held the viewer for the whole 3.8s animation with no way
             * out — the spin is already server-side and its outcome is already
             * recorded, so leaving costs the member nothing.
             */
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, close]);

    /* No spin to take, nothing to announce. The prompt IS the notification. */
    if (spinsRemaining <= 0 && !open) return null;
    /*
     * A FAILED FETCH IS NOT AN UNCONFIGURED WHEEL.
     *
     * This was `if (!count) return null`, which read a network error as "this
     * surface has no wheel": the prompt never appeared even with spins in hand,
     * and a panel already open vanished mid-session with nothing said. Only a
     * wheel that genuinely loaded with zero slices stays silent — that one IS
     * unconfigured, and announcing it would be announcing an operator's
     * half-finished setup to a member.
     */
    if (!count && !wheelLoading && !wheelFailed) return null;

    return (
        <>
            {spinsRemaining > 0 && !open &&
                <button type="button" className="sh-wfab" onClick={() => setOpen(true)}>
                    <span className="sh-wfab__in">
                        <PromptWheel />
                        <span className="sh-wfab__l">{promptLabel}</span>
                    </span>
                </button>}

            {open &&
                <div className="sh-whm" role="dialog" aria-modal="true" aria-label={title}>
                    <button
                        type="button"
                        className="sh-whm__scrim"
                        aria-label="Close"
                        onClick={close}
                    />
                    <div className="sh-whm__p">
                        <div className="sh-whm__in">
                            <button
                                type="button"
                                className="sh-whm__x"
                                aria-label="Close"
                                onClick={close}
                            >
                                ×
                            </button>
                            <span className="sh-whm__k">{kicker}</span>
                            <div className="sh-whm__t">{title}</div>

                            {/*
                              * THE shared wheel — the same `SpinWheel` the
                              * daily modal and the gamification renderer draw,
                              * skinned "flat" for a branded surface. (Admin's
                              * editor and Test dialog run a mirrored copy,
                              * `SpinWheelPreview`, because web and admin share
                              * no component package.) This modal owns chrome
                              * and state; it never draws the wheel itself.
                              */}
                            {count > 0 &&
                                <div className="sh-wsp">
                                    <span className="sh-wsp__pin" />
                                    {/* The wheel IS a button — the hub says SPIN, so pressing it must spin. */}
                                    <button
                                        type="button"
                                        className="sh-wsp__hit"
                                        onClick={onSpin}
                                        disabled={spinning || spinsRemaining <= 0}
                                        aria-label={spinning ? "Spinning" : "Spin the wheel"}
                                    >
                                        <SpinWheel
                                            segments={segments}
                                            rotation={rotation}
                                            size={320}
                                            hubLabel="SPIN"
                                            spinDurationMs={spinning ? SPIN_MS : 0}
                                            variant="flat"
                                        />
                                    </button>
                                </div>}

                            {/*
                              * The wheel could not be drawn. Says so, rather
                              * than presenting an empty panel the member reads
                              * as a broken spin.
                              */}
                            {!count &&
                                <p className="sh-whm__err" role={wheelFailed ? "alert" : "status"}>
                                    {wheelFailed
                                        ? "We couldn't load the wheel just now. Close this and try again in a moment."
                                        : "Loading your wheel…"}
                                </p>}

                            {error &&
                                <p className="sh-whm__err" role="alert">{error}</p>}

                            {!result && count > 0 &&
                                <button
                                    type="button"
                                    className="sh-whm__go"
                                    onClick={onSpin}
                                    disabled={spinning || spinsRemaining <= 0}
                                >
                                    {spinning ? "Spinning" : "Spin"}
                                </button>}

                            {result &&
                                <>
                                    {/*
                                      * role="status" — the win is the whole
                                      * point of the panel, and it appeared
                                      * silently for a screen reader while the
                                      * failure path was announced.
                                      */}
                                    <div className="sh-whm__res" role="status">
                                        <b>{result.outcomeLabel}</b>
                                        <span>{resultDetail(result)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="sh-whm__go sh-whm__go--done"
                                        onClick={close}
                                    >
                                        Done
                                    </button>
                                </>}
                        </div>
                    </div>
                </div>}
        </>
    );
};

export default SurfaceWheel;
